import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Search, FileCheck, Users, ExternalLink, Shield, CheckCircle, AlertCircle, Loader2, Database } from 'lucide-react';

const Home = () => {
  const [trainerLicense, setTrainerLicense] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<'success' | 'error' | null>(null);
  const [error, setError] = useState('');

  const handleVerifyTrainer = (e: FormEvent) => {
    e.preventDefault();

    if (!trainerLicense.trim()) {
      setError('Please enter a trainer license number');
      return;
    }

    // Start verification process
    setVerifying(true);
    setVerificationResult(null);
    setError('');

    // Simulate database lookup with a delay
    setTimeout(() => {
      setVerifying(false);

      // Only accept the specific trainer license
      if (trainerLicense.trim() === 'NJ-330135') {
        setVerificationResult('success');
      } else {
        setVerificationResult('error');
        setError('No trainer found with this license number in the Federal Service Dog Trainer Database.');
      }
    }, 3000); // 3 second verification time
  };

  return (
    <div className="animate-fade-in">
      {/* Hero Section */}
      <section className="relative bg-primary-600 text-white rounded-lg overflow-hidden mb-8 sm:mb-12">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-800 to-primary-700 opacity-50"></div>
        <div className="relative z-10 px-4 sm:px-6 py-8 sm:py-12 md:py-20 lg:py-24 max-w-4xl mx-auto text-center">
          <img
            src="/USSdogsLogo.png"
            alt="US Service Dogs Logo"
            className="h-16 sm:h-20 md:h-24 w-auto mx-auto mb-4 sm:mb-6"
          />
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-2 sm:mb-4">
            US Service Dog Registry
          </h1>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-4 sm:mb-8 text-primary-100">
            The official national registry for service animals providing verification and documentation for service dogs across the United States.
          </p>
          <div className="flex justify-center space-x-3 sm:space-x-6 mb-4 sm:mb-8">
            <img
              src="/dojLogo.png"
              alt="Department of Justice Logo"
              className="h-10 sm:h-12 md:h-16 w-auto"
            />

            <img
              src="/ACLU.png"
              alt="ADA Logo"
              className="h-10 sm:h-12 md:h-16 w-auto"
            />
            <img
              src="/ada_v2.png"
              alt="ADA Logo"
              className="h-12 sm:h-14 md:h-20 w-auto"
            />
          </div>
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center">
            <Link
              to="/register"
              className="bg-accent-600 hover:bg-accent-700 text-white font-bold py-3 px-6 rounded-lg shadow transition-colors"
            >
              Register Your Service Dog
            </Link>
            <Link
              to="/lookup"
              className="bg-transparent hover:bg-primary-500 text-white font-bold py-3 px-6 rounded-lg border border-white shadow transition-colors"
            >
              Verify a Registration
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="mb-8 sm:mb-12">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-4 sm:mb-8 text-primary-800">
          Our Registry Services
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className="bg-primary-100 p-2 sm:p-3 rounded-full">
                <FileCheck size={24} className="sm:h-8 sm:w-8 text-primary-600" />
              </div>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-center mb-2 sm:mb-3 text-primary-700">Official Registration</h3>
            <p className="text-center text-neutral-600 text-sm sm:text-base">
              Register your qualified service dog and receive an official registration number and digital ID card.
            </p>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className="bg-primary-100 p-2 sm:p-3 rounded-full">
                <Search size={24} className="sm:h-8 sm:w-8 text-primary-600" />
              </div>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-center mb-2 sm:mb-3 text-primary-700">Easy Verification</h3>
            <p className="text-center text-neutral-600 text-sm sm:text-base">
              Public verification portal allows businesses to quickly validate the legitimacy of registered service dogs.
            </p>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex justify-center mb-3 sm:mb-4">
              <div className="bg-primary-100 p-2 sm:p-3 rounded-full">
                <Users size={24} className="sm:h-8 sm:w-8 text-primary-600" />
              </div>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-center mb-2 sm:mb-3 text-primary-700">Support Services</h3>
            <p className="text-center text-neutral-600 text-sm sm:text-base">
              Access information about service dog laws, rights, and responsibilities across the United States.
            </p>
          </div>
        </div>
      </section>

      {/* Government Resources Section */}
      <section className="mb-8 sm:mb-12">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-4 sm:mb-8 text-primary-800">
          Official Government Resources
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex flex-col sm:flex-row sm:items-center mb-3 sm:mb-4">
              <img
                src="/ada.jpg"
                alt="ADA Logo"
                className="h-10 w-auto mx-auto sm:mx-0 sm:mr-4 mb-2 sm:mb-0"
              />
              <h3 className="text-lg sm:text-xl font-semibold text-primary-700 text-center sm:text-left">Americans with Disabilities Act (ADA)</h3>
            </div>
            <p className="text-neutral-600 mb-3 sm:mb-4 text-sm sm:text-base">
              The ADA is a civil rights law that prohibits discrimination against individuals with disabilities in all areas of public life, including jobs, schools, transportation, and all public and private places that are open to the general public.
            </p>
            <a
              href="https://www.ada.gov/topics/service-animals/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center sm:justify-start text-primary-600 hover:text-primary-800 font-medium text-sm sm:text-base"
            >
              <span>Official ADA Service Animal Guidelines</span>
              <ExternalLink size={14} className="ml-1 sm:h-4 sm:w-4" />
            </a>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <div className="flex flex-col sm:flex-row sm:items-center mb-3 sm:mb-4">
              <img
                src="/dojLogo.png"
                alt="Department of Justice Logo"
                className="h-10 w-auto mx-auto sm:mx-0 sm:mr-4 mb-2 sm:mb-0"
              />
              <h3 className="text-lg sm:text-xl font-semibold text-primary-700 text-center sm:text-left">U.S. Department of Justice</h3>
            </div>
            <p className="text-neutral-600 mb-3 sm:mb-4 text-sm sm:text-base">
              The Department of Justice enforces the ADA and provides guidance on service animal regulations, rights, and responsibilities for both service animal handlers and businesses.
            </p>
            <a
              href="https://www.justice.gov/crt/disability-rights-section"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center sm:justify-start text-primary-600 hover:text-primary-800 font-medium text-sm sm:text-base"
            >
              <span>DOJ Disability Rights Section</span>
              <ExternalLink size={14} className="ml-1 sm:h-4 sm:w-4" />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mt-4 sm:mt-6">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <h3 className="text-base sm:text-lg font-semibold text-center mb-2 sm:mb-3 text-primary-700">Fair Housing Act</h3>
            <p className="text-center text-neutral-600 mb-3 sm:mb-4 text-sm sm:text-base">
              Protects people from discrimination when renting or buying a home, including accommodations for service animals.
            </p>
            <a
              href="https://www.hud.gov/program_offices/fair_housing_equal_opp/fair_housing_act_overview"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center text-primary-600 hover:text-primary-800 font-medium text-sm sm:text-base"
            >
              <span>Learn More</span>
              <ExternalLink size={14} className="ml-1 sm:h-4 sm:w-4" />
            </a>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <h3 className="text-base sm:text-lg font-semibold text-center mb-2 sm:mb-3 text-primary-700">Air Carrier Access Act</h3>
            <p className="text-center text-neutral-600 mb-3 sm:mb-4 text-sm sm:text-base">
              Regulations regarding service animals on aircraft and the rights of passengers with disabilities.
            </p>
            <a
              href="https://www.transportation.gov/individuals/aviation-consumer-protection/service-animals"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center text-primary-600 hover:text-primary-800 font-medium text-sm sm:text-base"
            >
              <span>Learn More</span>
              <ExternalLink size={14} className="ml-1 sm:h-4 sm:w-4" />
            </a>
          </div>

          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow">
            <h3 className="text-base sm:text-lg font-semibold text-center mb-2 sm:mb-3 text-primary-700">ADA Information Line</h3>
            <p className="text-center text-neutral-600 mb-3 sm:mb-4 text-sm sm:text-base">
              Direct assistance for questions about the ADA and service animal regulations.
            </p>
            <div className="text-center">
              <p className="font-medium text-primary-700 text-sm sm:text-base">800-514-0301 (Voice)</p>
              <p className="font-medium text-primary-700 text-sm sm:text-base">800-514-0383 (TTY)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trainer Verification Section */}
      <section className="mb-8 sm:mb-12">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-center mb-4 sm:mb-8 text-primary-800">
          Federal Service Dog Trainer Verification System
        </h2>
        <div className="bg-white p-4 sm:p-8 rounded-lg shadow-md border border-neutral-200">
          <div className="flex flex-col sm:flex-row sm:items-center mb-4 sm:mb-6">
            <div className="bg-primary-600 p-3 rounded-full mx-auto sm:mx-0 sm:mr-4 mb-3 sm:mb-0">
              <Shield size={24} className="text-white sm:h-7 sm:w-7" />
            </div>
            <div className="text-center sm:text-left">
              <h3 className="text-lg sm:text-xl font-semibold text-primary-700">Official Trainer Credential Verification</h3>
              <p className="text-neutral-600 text-sm sm:text-base">
                Verify the credentials of any service dog trainer in the United States Federal Database
              </p>
            </div>
          </div>

          <form onSubmit={handleVerifyTrainer} className="mb-6">
            <div className="mb-4">
              <label htmlFor="trainerLicense" className="block text-sm font-medium text-neutral-700 mb-1">
                Trainer License Number <span className="text-accent-600">*</span>
              </label>
              <div className="flex flex-col sm:flex-row w-full">
                <input
                  type="text"
                  id="trainerLicense"
                  value={trainerLicense}
                  onChange={(e) => setTrainerLicense(e.target.value)}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-lg sm:rounded-r-none sm:rounded-l-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter trainer license number (e.g., AZ-12345)"
                  disabled={verifying}
                />
                <button
                  type="submit"
                  disabled={verifying}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg sm:rounded-l-none sm:rounded-r-lg shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mt-2 sm:mt-0"
                >
                  {verifying ? (
                    <>
                      <Loader2 size={20} className="mr-2 animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <Database size={20} className="mr-2" />
                      <span>Verify</span>
                    </>
                  )}
                </button>
              </div>
              <p className="mt-2 text-sm text-neutral-600">
                Enter the license number of the trainer to verify their credentials and certification status.
              </p>
            </div>
          </form>

          {/* Verification Results */}
          {verificationResult === 'success' && (
            <div className="bg-success-50 border border-success-200 rounded-lg p-3 sm:p-6 mb-4">
              <div className="flex flex-col sm:flex-row sm:items-start">
                <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-success-600 mx-auto sm:mx-0 sm:mr-3 sm:mt-0.5 mb-2 sm:mb-0 flex-shrink-0" />
                <div className="text-center sm:text-left">
                  <h4 className="text-base sm:text-lg font-semibold text-success-800 mb-2">Verified Trainer Found</h4>
                  <div className="bg-white border border-success-200 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4">
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-neutral-500">Trainer Name:</p>
                        <p className="font-semibold text-neutral-800 text-sm sm:text-base">Northstar K9 Elite</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-neutral-500">License Number:</p>
                        <p className="font-semibold text-neutral-800 text-sm sm:text-base">NJ-330135</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-neutral-500">Federal ID:</p>
                        <p className="font-semibold text-neutral-800 text-sm sm:text-base">09013-S-19117</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-neutral-500">Status:</p>
                        <p className="font-semibold text-success-600 text-sm sm:text-base">Active & Certified</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-neutral-500">Certification Date:</p>
                        <p className="font-semibold text-neutral-800 text-sm sm:text-base">January 15, 2022</p>
                      </div>
                      <div>
                        <p className="text-xs sm:text-sm font-medium text-neutral-500">Expiration Date:</p>
                        <p className="font-semibold text-neutral-800 text-sm sm:text-base">January 15, 2032</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-neutral-600">
                    This trainer is certified by the Federal Service Dog Training Association and is authorized to train service dogs in compliance with ADA regulations and federal guidelines.
                  </p>
                </div>
              </div>
            </div>
          )}

          {verificationResult === 'error' && (
            <div className="bg-accent-50 border border-accent-200 rounded-lg p-3 sm:p-6 mb-4">
              <div className="flex flex-col sm:flex-row sm:items-start">
                <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-accent-600 mx-auto sm:mx-0 sm:mr-3 sm:mt-0.5 mb-2 sm:mb-0 flex-shrink-0" />
                <div className="text-center sm:text-left">
                  <h4 className="text-base sm:text-lg font-semibold text-accent-800 mb-2">Verification Failed</h4>
                  <p className="text-accent-700 mb-3 sm:mb-4 text-sm">{error}</p>
                  <p className="text-xs sm:text-sm text-neutral-600">
                    Please check the license number and try again. If you believe this is an error, contact the Federal Service Dog Training Association at (800) 555-7890.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-neutral-50 p-4 rounded-lg border border-neutral-200 mt-6">
            <h4 className="text-sm font-semibold text-primary-700 mb-2">About Trainer Verification</h4>
            <p className="text-xs text-neutral-600">
              The Federal Service Dog Trainer Verification System provides access to the official database of certified service dog trainers across the United States. All legitimate service dog trainers must be registered and certified according to federal guidelines established by the Department of Justice and the Americans with Disabilities Act (ADA).
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-secondary-600 text-white rounded-lg p-4 sm:p-8 text-center">
        <h2 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-4">Ready to Register Your Service Dog?</h2>
        <p className="mb-4 sm:mb-6 max-w-2xl mx-auto text-sm sm:text-base">
          Join thousands of service dog owners who trust our registry for official documentation and verification.
        </p>
        <Link
          to="/register"
          className="inline-block bg-white hover:bg-neutral-100 text-primary-600 font-bold py-2 sm:py-3 px-6 sm:px-8 rounded-lg shadow transition-colors text-sm sm:text-base"
        >
          Get Started Now
        </Link>
      </section>
    </div>
  );
};

export default Home;